import React, { useState, useEffect } from 'react';
import { X, ShoppingCart, Info } from 'lucide-react';
import { useTheme } from '../core/ui/ThemeContext';
import { useLanguage } from '../core/i18n/LanguageContext';
import LiquidGlassButton from './LiquidGlassButton';
import MappingAutocomplete from './common/MappingAutocomplete';
import { KONTIERUNGSTYPEN, isKontierungFieldVisible, isKontierungFieldRequired, glAccountKnttpFor } from '../core/utils/kontierung';
import useVendorNames from '../core/ui/useVendorNames';

/**
 * MissingFieldsModal ("Position ergänzen") — ergänzt fehlende Kontierungs-/Stammfelder
 * bevor eine Position in den Warenkorb/die BANF übernommen wird.
 *
 * Props:
 *   isOpen, onClose, onConfirm(fields), product, missingFields, user
 *   dynamicKontierung  boolean  Default TRUE: Pflicht- UND sichtbare Felder richten sich
 *                               dynamisch nach dem gewählten Kontierungstyp (Spiegel der
 *                               BANF-Logik CreateBanfView.getMissingFields). EINE zentrale
 *                               Logik — identisch für Katalog/OCI UND Lager, damit das Modal
 *                               überall gleich funktioniert.
 *                               Nur explizit `false` setzen für den alten statischen
 *                               missingFields-Modus (Legacy/Sonderfall).
 */
const MissingFieldsModal = ({
  isOpen,
  onClose,
  onConfirm,
  product,
  missingFields,
  user,
  dynamicKontierung = true,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // Primäre Bezugsquelle aus product.sources[]
  const primarySrc = Array.isArray(product?.sources)
    ? (product.sources.find(s => s.is_primary) || product.sources[0])
    : null;

  const [costCenter, setCostCenter] = useState('');
  const [purchasingOrg, setPurchasingOrg] = useState('');
  const [plant, setPlant] = useState('');
  const [glAccount, setGlAccount] = useState('');
  const [einkauefergruppe, setEinkauefergruppe] = useState('');
  const [kontierungstyp, setKontierungstyp] = useState('kostenstelle');
  const [preferredVendor, setPreferredVendor] = useState('');
  const [auftrag, setAuftrag] = useState('');

  // Reset bei jedem Öffnen / neuem Produkt
  useEffect(() => {
    if (isOpen && product) {
      setCostCenter(product?.cost_center || user?.costCenter || '');
      setPurchasingOrg(product?.purchasing_org || user?.purchasingOrg || '');
      setPlant(product?.plant || user?.plant || '');
      setGlAccount(product?.gl_account || '');
      setEinkauefergruppe(product?.purchasing_group || user?.purchasingGroup || '');
      setKontierungstyp(product?.kontierungstyp || 'kostenstelle');
      setPreferredVendor(
        product?.preferred_vendor ||
        primarySrc?.vendor_name ||
        ''
      );
      setAuftrag(product?.auftrag || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, product, user]);

  const token = user?.token;
  const vendorNameFor = useVendorNames([preferredVendor], token);

  // Pflichtfeld je Kontierungstyp (dynamisch), sonst missingFields-basiert.
  // Spiegelt CreateBanfView.getMissingFields:
  //   kostenstelle -> Kostenstelle + Sachkonto ; auftrag -> + Auftrag ;
  //   material -> keine Kontierung.
  // Einkaufsorganisation ist im dynamischen Modus Basis-Pflicht; Werk vorbefüllt/optional.
  const isRequired = (field) => {
    if (!dynamicKontierung) return Array.isArray(missingFields) && missingFields.includes(field);
    return isKontierungFieldRequired(kontierungstyp, field);
  };

  // Sichtbarkeit je Kontierungstyp (nur im dynamischen Modus eingeschränkt).
  const isVisible = (field) => {
    if (!dynamicKontierung) return field !== 'auftrag'; // Auftrag-Feld nur dynamisch
    return isKontierungFieldVisible(kontierungstyp, field);
  };

  // Validierung: nur Pflichtfelder blockieren Submit
  const allRequiredFilled = () => {
    if (isRequired('cost_center') && !costCenter) return false;
    if (isRequired('purchasing_org') && !purchasingOrg) return false;
    if (isRequired('plant') && !plant) return false;
    if (isRequired('auftrag') && !auftrag) return false;
    if (isRequired('gl_account') && !glAccount) return false;
    return true;
  };

  const handleConfirm = () => {
    if (!allRequiredFilled()) return;
    // Nur sichtbare Felder übernehmen — sonst wandern vorbefüllte, aber für den
    // gewählten Typ ausgeblendete Werte (z. B. Kostenstelle bei 'material') in die Zeile
    onConfirm({
      cost_center: isVisible('cost_center') ? costCenter : '',
      purchasing_org: purchasingOrg,
      plant: plant,
      gl_account: isVisible('gl_account') ? glAccount : '',
      einkauefergruppe: einkauefergruppe,
      kontierungstyp: kontierungstyp,
      preferred_vendor: preferredVendor,
      auftrag: isVisible('auftrag') ? auftrag : '',
    });
  };

  if (!isOpen) return null;

  const hasRequired =
    isRequired('cost_center') || isRequired('purchasing_org') || isRequired('plant') ||
    isRequired('auftrag') || isRequired('gl_account');

  const labelClass = `block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-white/90' : 'text-slate-700'}`;
  const optionalBadge = (
    <span className={`ml-1.5 text-xs font-normal ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
      {t('missingFields.optional')}
    </span>
  );
  const reqMark = <span className="text-red-500"> *</span>;
  const inputWrapBg = isDarkMode
    ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl'
    : 'bg-white border border-slate-200';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className={`relative w-full max-w-lg rounded-3xl overflow-hidden shadow-lg max-h-[90vh] flex flex-col ${
        isDarkMode
          ? 'bg-gray-900/90 border border-white/20 backdrop-blur-xl'
          : 'bg-white border border-slate-200'
      }`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b flex-shrink-0 ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-brand/20' : 'bg-brand/10'}`}>
                <ShoppingCart className={`w-5 h-5 ${isDarkMode ? 'text-brand' : 'text-brand'}`} />
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {t('missingFields.titlePrepare')}
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                  {t('missingFields.subtitlePrepare')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-xl transition-colors ${
                isDarkMode ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-400'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Produkt-Info */}
        <div className={`px-6 py-2.5 flex-shrink-0 ${isDarkMode ? 'bg-blue-900/20' : 'bg-brand/5 border-b border-slate-100'}`}>
          <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-brand'}`}>
            <span className="font-medium">{t('missingFields.article')}</span>{' '}
            {product?.name || product?.description || t('missingFields.unnamedFallback')}
          </p>
        </div>

        {/* Hinweis-Banner wenn es Pflichtfelder gibt */}
        {hasRequired && (
          <div className={`mx-6 mt-4 flex-shrink-0 flex items-start gap-2 px-3 py-2 rounded-xl text-sm ${
            isDarkMode ? 'bg-orange-900/30 text-orange-300 border border-orange-700/40' : 'bg-orange-50 text-orange-700 border border-orange-200'
          }`}>
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{t('missingFields.requiredHint')}</span>
          </div>
        )}

        {/* Scrollbarer Feldbereich */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">

          {/* Kontierungstyp — steuert im dynamischen Modus Pflicht-/sichtbare Felder */}
          <div>
            <label className={labelClass}>
              {t('missingFields.kontierungstyp')}{optionalBadge}
            </label>
            <div className="relative">
              <div className={`absolute inset-0 ${inputWrapBg} rounded-2xl`} />
              <select
                value={kontierungstyp}
                onChange={e => setKontierungstyp(e.target.value)}
                className={`relative w-full px-3 py-2.5 bg-transparent border-0 rounded-2xl focus:ring-2 focus:ring-brand/40 focus:outline-none ${isDarkMode ? 'text-white' : 'text-slate-900'}`}
              >
                {KONTIERUNGSTYPEN.map(opt => (
                  <option key={opt.value} value={opt.value} className={isDarkMode ? 'bg-gray-800' : 'bg-white'}>
                    {t(opt.labelKey)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Auftrag (nur Kontierungstyp 'auftrag') — Live-Suche AUFK wie im BANF-Formular */}
          {isVisible('auftrag') && (
            <div>
              <label className={labelClass}>
                {t('missingFields.auftrag')}{isRequired('auftrag') ? reqMark : optionalBadge}
              </label>
              <MappingAutocomplete
                helpId="order"
                context={{ werks: plant }}
                minChars={2}
                value={auftrag}
                onChange={setAuftrag}
                placeholder={t('missingFields.auftragPlaceholder')}
                token={token}
                isDarkMode={isDarkMode}
              />
            </div>
          )}

          {/* Kostenstelle — Live-Wertehilfe CSKS/CSKT (Registry-Eintrag aus PR #62);
              Schnappschuss bleibt als Ausfall-Rückfall im Backend hinterlegt */}
          {isVisible('cost_center') && (
            <div>
              <label className={labelClass}>
                {t('missingFields.costCenterLabel')}{isRequired('cost_center') ? reqMark : optionalBadge}
              </label>
              <MappingAutocomplete
                helpId="cost_center"
                context={{ werks: plant }}
                minChars={0}
                value={costCenter}
                onChange={setCostCenter}
                placeholder={t('missingFields.searchCostCenter')}
                token={token}
                isDarkMode={isDarkMode}
              />
            </div>
          )}

          {/* Sachkonto — Live-Wertehilfe, auf Kostenelemente des Kontierungstyps eingeschränkt */}
          {isVisible('gl_account') && (
            <div>
              <label className={labelClass}>
                {t('missingFields.glAccount')}{isRequired('gl_account') ? reqMark : optionalBadge}
              </label>
              <MappingAutocomplete
                helpId="gl_account"
                context={{ knttp: glAccountKnttpFor(kontierungstyp) }}
                minChars={0}
                value={glAccount}
                onChange={setGlAccount}
                placeholder={t('missingFields.searchGlAccount')}
                token={token}
                isDarkMode={isDarkMode}
              />
            </div>
          )}

          {/* Einkäuferorganisation */}
          {isVisible('purchasing_org') && (
            <div>
              <label className={labelClass}>
                {t('missingFields.purchasingOrgLabel')}{isRequired('purchasing_org') ? reqMark : optionalBadge}
              </label>
              <MappingAutocomplete
                type="purchasing_org"
                value={purchasingOrg}
                onChange={setPurchasingOrg}
                placeholder={t('missingFields.searchPurchasingOrg')}
                token={token}
                isDarkMode={isDarkMode}
              />
            </div>
          )}

          {/* Werk */}
          {isVisible('plant') && (
            <div>
              <label className={labelClass}>
                {t('missingFields.plantLabel')}{isRequired('plant') ? reqMark : optionalBadge}
              </label>
              <MappingAutocomplete
                type="plant"
                value={plant}
                onChange={setPlant}
                placeholder={t('missingFields.searchPlant')}
                token={token}
                isDarkMode={isDarkMode}
              />
            </div>
          )}

          {/* Einkäufergruppe */}
          <div>
            <label className={labelClass}>
              {t('missingFields.purchasingGroup')}{optionalBadge}
            </label>
            <MappingAutocomplete
              type="purchasing_group"
              value={einkauefergruppe}
              onChange={setEinkauefergruppe}
              placeholder={t('missingFields.searchPurchasingGroup')}
              token={token}
              isDarkMode={isDarkMode}
            />
          </div>

          {/* Wunschlieferant — Live-Namenssuche LFA1; Auswahl speichert die LIFNR,
              damit der Mapper sie als Wunschlieferant an SAP übergeben kann */}
          <div>
            <label className={labelClass}>
              {t('missingFields.preferredVendor')}{optionalBadge}
            </label>
            <MappingAutocomplete
              helpId="vendor"
              minChars={2}
              value={preferredVendor}
              onChange={setPreferredVendor}
              placeholder={t('missingFields.preferredVendorPlaceholder')}
              token={token}
              isDarkMode={isDarkMode}
            />
            {vendorNameFor(preferredVendor) && (
              <p className={`mt-1 text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
                {vendorNameFor(preferredVendor)}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t flex-shrink-0 ${isDarkMode ? 'border-white/10' : 'border-gray-200'} flex justify-end space-x-3`}>
          <LiquidGlassButton onClick={onClose} variant="secondary">
            {t('common.cancel')}
          </LiquidGlassButton>
          <LiquidGlassButton onClick={handleConfirm} variant="success" disabled={!allRequiredFilled()}>
            <ShoppingCart className="w-4 h-4 mr-2" />
            {t('missingFields.addToCart')}
          </LiquidGlassButton>
        </div>
      </div>
    </div>
  );
};

export default MissingFieldsModal;
